import { useShow, useTranslate } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { theme } from 'antd';
import type { PublisherDetailOut } from '@/api/generated/schemas';
import { bareShowContentProps } from '@/components/common/show/shared';
import { useShowTab } from '@/components/common/show/useShowTab';
import { BulkModStatusControl } from '@/components/user_catalog/BulkModStatusControl';
import { useRecordDocumentTitle } from '@/hooks/useRecordDocumentTitle';
import { PUBLISHER_DEFAULT_TAB, PUBLISHER_SHOW_TABS } from '../constants';
import { PublisherShowHeader } from './PublisherShowHeader';
import { PublisherGeneralTab } from './PublisherGeneralTab';
import { PublisherCollectionTab } from './PublisherCollectionTab';
import { SearchPopularityCard } from '@/components/common/show/SearchPopularityCard';
import { CollectionPublicationsTable } from '@/components/user_catalog/collections/CollectionPublicationsTable';

export const PublisherShow = () => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const { query } = useShow<PublisherDetailOut>({
    resource: 'user_catalog.publishers',
  });
  const { data, isLoading } = query;
  const record = data?.data;

  useRecordDocumentTitle('user_catalog.publishers', record?.name);

  const [activeTab, onTabChange] = useShowTab(
    PUBLISHER_SHOW_TABS,
    PUBLISHER_DEFAULT_TAB
  );

  return (
    <Show
      isLoading={isLoading}
      contentProps={bareShowContentProps}
      title={
        record?.parent_id != null
          ? t('user_catalog.publishers.titles.show_imprint')
          : undefined
      }
      headerButtons={({ defaultButtons }) => (
        <>
          {record?.id != null && (
            <BulkModStatusControl
              resource="user_catalog.publishers"
              ids={[record.id]}
              onSuccess={() => query.refetch()}
            />
          )}
          {defaultButtons}
        </>
      )}
    >
      {record && (
        <>
          <PublisherShowHeader
            record={record}
            activeTab={activeTab}
            onTabChange={onTabChange}
          />
          <div style={{ marginTop: token.margin }}>
            {activeTab === 'general' && (
              <PublisherGeneralTab
                record={record}
                onSynced={() => query.refetch()}
              />
            )}
            {activeTab === 'publications' && (
              <CollectionPublicationsTable
                collectionId={record.id}
                collectionKind={record.collection_kind}
              />
            )}
            {activeTab === 'collection' && (
              <PublisherCollectionTab record={record} />
            )}
            {activeTab === 'search' && (
              <SearchPopularityCard value={record.search_popularity} />
            )}
          </div>
        </>
      )}
    </Show>
  );
};
